import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import GanttChart from '../../../components/Gantt/GanttChart';
import { GanttViewMode } from '../../../components/Gantt/types/types';
import { useImprovementPlansGantt } from '../hooks/useImprovementPlansGantt';
import GanttActionEditModal from '../components/modals/GanttActionEditModal';

const MS_DAY = 86400000;
const diffDays = (a: Date, b: Date) => Math.round((a.getTime() - b.getTime()) / MS_DAY);
const toISO = (d: Date) => {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

interface GanttTask {
  id: number | string;
  name: string;
  parentId: number | string | null;
  start?: string;       // ISO
  duration?: number;    // days
  progress?: number;
  type?: 'task' | 'milestone';
  expanded?: boolean;
  status?:string;
}

export default function ImprovementPlansGantt() {
  const { t } = useTranslation('improvementPlan');
  const navigate = useNavigate();
  const { data, isLoading, error } = useImprovementPlansGantt();
  const [viewMode, setViewMode] = useState<GanttViewMode>('months');
  const [editingAction, setEditingAction] = useState<{ planId: number; actionId: number } | null>(null);

  const plans = data?.plans ?? [];
  const accions = data?.accions ?? {};
  const initialTasks = useMemo<GanttTask[]>(() => {
  const result: GanttTask[] = [];
  plans.forEach(p => {
  result.push({
    id: p.id,
    name: p.titol,
    parentId: null,
    expanded: false,
    start: toISO(new Date(p.dataInici)),
    duration: Math.max(0, diffDays(new Date(p.dataFi), new Date(p.dataInici))),
    status: p.estat,        // plan status
  });

  (accions[p.id] ?? []).forEach(a => {
    result.push({
      id: `a-${a.id}`,
      name: a.descripcio,
      parentId: p.id,
      start: toISO(new Date(a.dataInici)),
      duration: Math.max(0, diffDays(new Date(a.dataFi), new Date(a.dataInici))),
      status: a.estat,      // action status
    });
  });
});

  return result;
}, [plans, accions]);
  // GanttChart needs tasks + setTasks (it mutates internally on drag/edit)
  const [tasks, setTasks] = useState<GanttTask[]>(initialTasks);

  // Re-sync local state when the fetched data changes
  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  const handleSchedule = (id: number | string, schedule: unknown) => {
    console.warn('onSchedule not wired to backend', id, schedule);
  };
  const handleSave = (id: number | string, payload: unknown) => {
    console.warn('onSave not wired to backend', id, payload);
  };
  const handleReorder = (id: number | string, targetId: number | string) => {
    console.warn('onReorder not wired to backend', id, targetId);
  };

  const allExpanded = useMemo(
    () => {
      const roots = tasks.filter((task) => task.parentId === null);
      return roots.length > 0 && roots.every((task) => task.expanded);
    },
    [tasks]
  );

  const handleToggleExpandAll = () => {
    const next = !allExpanded;
    setTasks((prev) =>
      prev.map((task) =>
        task.parentId === null ? { ...task, expanded: next } : task
      )
    );
  }; 

  const handleActionClick = (id: number | string) => {
    const task = tasks.find((current) => current.id === id);
    if (!task || task.parentId == null) return;
    const actionId = Number(String(id).replace(/^a-/, ''));
    if (Number.isNaN(actionId)) return;
    setEditingAction({ planId: Number(task.parentId), actionId });
  };

  const handlePlanClick = (id: number | string) => {
    navigate(`/improvement-plans/${id}`);
  };

  if (isLoading) {
    return <Spinner animation="border" />;
  }
  if (error) {
    return <Alert variant="danger">Error carregant el diagrama</Alert>;
  }
  if (plans.length === 0) {
    return (
      <Alert variant="light" className="text-center">
        No hi ha plans amb els filtres aplicats.
      </Alert>
    );
  }

  return (
    <>
      <Form className="mb-3 d-flex align-items-center gap-3">
       
        <Button
          variant="secondary"
          className="d-inline-flex align-items-center"
          onClick={handleToggleExpandAll}
          aria-expanded={allExpanded}
        >
          <i className={`bi ${allExpanded ? 'bi-chevron-double-up' : 'bi-chevron-double-down'} me-2`}></i>
          {t(allExpanded ? 'common:buttons.collapse' : 'common:buttons.expand')}
        </Button>
         <Form.Check
          type="switch"
          id="gantt-view-switch"
          label={t('ganttView.byWeeks')}
          checked={viewMode === 'weeks'}
          onChange={(e) => setViewMode(e.target.checked ? 'weeks' : 'months')}
        />
      </Form>
      <Card>
        <GanttChart
          tasks={tasks}
          setTasks={setTasks}
          viewMode={viewMode}
          onSchedule={handleSchedule}
          onSave={handleSave}
          onReorder={handleReorder}
          onActionClick={handleActionClick}
          onPlanClick={handlePlanClick}
        />
      </Card>

      <GanttActionEditModal
        show={editingAction !== null}
        planId={editingAction?.planId ?? null}
        actionId={editingAction?.actionId ?? null}
        onClose={() => setEditingAction(null)}
      />
    </>
  );
}